import { Maps, LocationObject, LocationArray } from "./locationTypes"


// Helper function for empty results
const emptyLocationResult = {
  city: "",
  country: "",
  province: "",
}

// Helper function for string locations
const processStringLocation = (
  location: string,
  maps: Maps
): { city: string; country: string; province: string } => {
  const cityObj = maps.cityMap.get(location)
  if (cityObj) {
    const provinceObj = maps.provinceMap.get(cityObj.province)
    const countryObj = provinceObj ? maps.countryMap.get(provinceObj.country) : null
    return {
      city: cityObj.name,
      country: countryObj?.name || "",
      province: provinceObj?.name || "",
    }
  }

  const provinceObj = maps.provinceMap.get(location)
  if (provinceObj) {
    const countryObj = maps.countryMap.get(provinceObj.country)
    return {
      city: "",
      country: countryObj?.name || "",
      province: provinceObj.name,
    }
  }

  const countryObj = maps.countryMap.get(location)
  if (countryObj)
    return {
      city: "",
      country: countryObj.name,
      province: "",
    }

  return emptyLocationResult
}

// Helper function for object locations
const processObjectLocation = (
  location: LocationObject
): { city: string; country: string; province: string } => {
  const content = location.content

  if (!content) return emptyLocationResult

  if ("province" in content)
    return {
      city: content.name || "",
      country: typeof content.province === "object" ? content.province?.content?.country?.content?.name || "" : "",
      province: typeof content.province === "string" ? content.province : content.province?.content?.name || "",
    }

  if ("country" in content)
    return {
      city: "",
      country: content.country?.content?.name || "",
      province: content.name || "",
    }

  return {
    city: "",
    country: content.name || "",
    province: "",
  }
}

// Utility function to autofill related fields
export const autofillRelatedFields = (
  filters: { city?: string; province?: string; country?: string },
  maps: Maps
): { city: string; province: string; country: string } => {
  const updatedFilters = { city: filters.city || "", country: "", province: "" }

  if (filters.city) {
    const cityObj = Array.from(maps.cityMap.values()).find((city) => city.name === filters.city)
    if (cityObj) {
      const provinceObj = maps.provinceMap.get(cityObj.province)
      updatedFilters.province = provinceObj?.name || ""
      const countryObj = provinceObj ? maps.countryMap.get(provinceObj.country) : null
      updatedFilters.country = countryObj?.name || ""
    }
  } else if (filters.province) {
    const provinceObj = Array.from(maps.provinceMap.values()).find((province) => province.name === filters.province)
    if (provinceObj) {
      updatedFilters.province = provinceObj.name
      const countryObj = maps.countryMap.get(provinceObj.country)
      updatedFilters.country = countryObj?.name || ""
    }
  } else if (filters.country) {
    updatedFilters.country = filters.country
  }

  return updatedFilters
}

export const getCityProvinceCountry = (
  location: string | LocationObject | LocationArray,
  maps: Maps
): { city: string; country: string; province: string }[] => {
  if (!location) return [emptyLocationResult]

  // Handle array of locations
  if (Array.isArray(location))
    return location.map((loc) => getCityProvinceCountry(loc, maps)[0])

  // Handle string locations
  if (typeof location === "string")
    return [processStringLocation(location, maps)]

  // Handle object locations (including StoryblokStory)
  if (typeof location === "object" && location.content)
    return [processObjectLocation(location)]

  return [emptyLocationResult]
}

// Filter provinces based on selected country
export const getFilteredProvinces = (
  maps: Maps,
  selectedCountry: string
): string[] => {
  const { provinceMap, countryMap } = maps
  
  if (!selectedCountry)
    return Array.from(provinceMap.values())
      .map((province) => province.name)
      .sort((a, b) => a.localeCompare(b))

  const selectedCountryUuid = Array.from(countryMap.entries()).find(([uuid, country]) => country.name === selectedCountry)?.[0]
  return Array.from(provinceMap.values())
    .filter((province) => province.country === selectedCountryUuid)
    .map((province) => province.name)
    .sort((a, b) => a.localeCompare(b))
}

// Filter cities based on selected province
export const getFilteredCities = (
  maps: Maps,
  selectedProvince: string
): string[] => {
  const { cityMap, provinceMap } = maps

  if (!selectedProvince)
    return Array.from(cityMap.values())
      .map((city) => city.name)
      .sort((a, b) => a.localeCompare(b))

  const selectedProvinceUuid = Array.from(provinceMap.entries()).find(([uuid, province]) => province.name === selectedProvince)?.[0]
  return Array.from(cityMap.values())
    .filter((city) => city.province === selectedProvinceUuid)
    .map((city) => city.name)
    .sort((a, b) => a.localeCompare(b))
}
